import type { GridOptions } from 'ag-grid-community'
import dayjs from 'dayjs'
import { OrderType } from '../store/order/types'

export const getPlural = (count: number) => (count > 1 ? 's' : '')

export const formatCoupon = (coupon: number) =>
  Math.round(coupon * 10000) / 10000

export const getMonthAndYear = (date: Date) => {
  let parsedDate = date
  if (typeof date === 'string') {
    parsedDate = new Date(date)
  }
  const [year, month] = parsedDate.toISOString().split('-').map(Number)
  return { year, month }
}

export const formatMaturityDateAsText = (
  date: Date,
  withLeadingZero = true
) => {
  const { year, month } = getMonthAndYear(date)

  return `${`${withLeadingZero ? '0' : ''}${month}`.substr(-2, 2)}/${
    year % 100
  }`
}

export const fullMonth = [
  'Jan',
  'Febr',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec'
]

export const days = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday'
]

export const formatMaturityFullDate = (date: Date) => {
  let parsedDate = date
  if (typeof date === 'string') {
    parsedDate = new Date(date)
  }
  return `${parsedDate.getDate()}-${
    fullMonth[parsedDate.getMonth()]
  }-${parsedDate.getFullYear()}`
}

export const formatSecondtimer = (timer: number) => {
  return `${Math.floor(timer % 60)}`.padStart(2, '0')
}

export const secondsToMinutes = (time: number) =>
  Math.floor(time / 60) + ':' + formatSecondtimer(time)

export const ConvertUSTPrice = (priceStr: string) => {
  let ustPrice = 0
  const endsWithPlus = priceStr.endsWith('+')
  const arr = endsWithPlus
    ? priceStr.substring(0, priceStr.length - 1).split('-')
    : priceStr.split('-')
  if (arr.length === 2) {
    const dollarsStr = arr[0]
    const dollars = Number(dollarsStr)
    const centsStr = arr[1]
    const cents = Number(centsStr)

    let thirtySeconds: number
    let twoHundredFiftySixths: number
    if (!isNaN(dollars) && !isNaN(cents)) {
      if (endsWithPlus) {
        if (centsStr.length === 2) {
          thirtySeconds = Number(centsStr)
          ustPrice = dollars + thirtySeconds / 32 + 4 / 256
        }
      } else {
        if (centsStr.length === 2) {
          thirtySeconds = Number(centsStr)
          ustPrice = dollars + thirtySeconds / 32
        } else if (centsStr.length === 3) {
          thirtySeconds = Number(centsStr.substring(0, 2))
          twoHundredFiftySixths = Number(centsStr.substring(2, 3))
          ustPrice = dollars + thirtySeconds / 32 + twoHundredFiftySixths / 256
        }
      }
    }
  }
  return ustPrice
}

export const formatTreasuryPrice = (price: number) => {
  const truncated = Math.trunc(price)
  const fraction = price - truncated
  const number256ths = Math.trunc(fraction * 256)
  const number32nds = Math.trunc(fraction * 32)
  const remainder = number256ths - number32nds * 8
  let stRemainder = remainder.toString()

  if (stRemainder === '4') stRemainder = '+'
  else if (stRemainder === '0') stRemainder = ''

  return (
    truncated.toString() +
    '-' +
    number32nds.toString().padStart(2, '0') +
    stRemainder
  )
}

const currencyMap: Record<string, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£'
}

export const formatCurrency = (
  value: string | number | undefined,
  currencyAbbr: string = ''
) => {
  const symbol = value ? currencyMap[currencyAbbr] ?? '' : ''
  return `${symbol}${
    value?.toLocaleString(undefined, { maximumFractionDigits: 4 }) || ''
  }`
}

export const formatAggressorLLPrice = (
  price: number,
  currency: string = '',
  isTreasury: boolean = false
) => {
  let tsyPrice
  if (price && isTreasury) {
    tsyPrice = formatTreasuryPrice(price)
  }
  return formatCurrency(tsyPrice ?? price, currency)
}

export const abbreviateVolume = (value: number) => {
  let newValue = Intl.NumberFormat('en-US', {
    notation: 'compact'
  }).format(value)

  newValue = newValue.replace('M', 'mm')
  return newValue
}

const isNumeric = (val: string): boolean => {
  return !isNaN(Number(val))
}

export const dateFormatter = (params: { value: string }): string => {
  const date = dayjs(params.value)
  if (date.year() < 1900) {
    return ''
  }
  return date.format('M/DD/YY, h:mm a')
}

export const diffDateFormatter = (params: any) => {
  if (isNumeric(params.value)) {
    return params.value
  }
  const orderTime = dayjs(params.value)
  const diff = dayjs().diff(orderTime, 'days')

  if (diff > 0) {
    return orderTime.format('MMMM Do')
  } else {
    return orderTime.format('HH:mm:ss')
  }
}

export const formatSize = (
  size: number,
  totalSize: number,
  aon: boolean,
  isMine: boolean | string | undefined
) => {
  if (!size) return '-'

  const sizeAon = size + (aon ? 'a' : '')
  if (size >= totalSize) return sizeAon
  return size && isMine ? `${sizeAon} (${totalSize})` : `${sizeAon}`
}

const singleConditionFilterParams = {
  buttons: ['reset', 'apply'],
  closeOnApply: true,
  maxNumConditions: 1
}

/*
  AG Grid replaces defaultColDef.filterParams wholesale rather than merging into it, so a
  column opting into more conditions has to carry the buttons and close-on-apply too.
*/
export const multiConditionFilterParams = {
  ...singleConditionFilterParams,
  maxNumConditions: 5
}

export const defaultGridOptions: GridOptions = {
  defaultColDef: {
    resizable: true,
    sortable: true,
    filter: 'agTextColumnFilter',
    filterParams: singleConditionFilterParams,
    cellStyle: {
      border: 'var(--border)',
      borderWidth: '0 1px'
    }
  },
  headerHeight: 30,
  rowHeight: 30,
  columnMenu: 'legacy',
  alwaysShowVerticalScroll: true
}

// ------------------ Text Formatting ------------------ //
export const formatToCamelCase = (str: string) => {
  return str
    .replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) =>
      index === 0 ? word.toLowerCase() : word.toUpperCase()
    )
    .replace(/\s+/g, '')
}

export const formatToWords = (str: string) => {
  return str.replace(
    /([a-z0-9])(?=[A-Z])|([A-Za-z])(?=\d)|(\d)(?=[A-Za-z])/g,
    '$& '
  )
}

export const isSpreadToLabel = (isSpread: boolean) =>
  isSpread ? 'Spread' : 'Price'

export const getOppositeDirectionCode = (direction: OrderType) =>
  direction.toLowerCase() === 'buy' ? 'SXT' : 'BXT'

export const formatVconData = ({
  isin,
  corpGovt,
  direction,
  amount,
  markedPrice
}: {
  isin: string
  corpGovt: string
  direction: OrderType
  amount: string | number
  markedPrice: string | undefined
}) =>
  [
    isin,
    corpGovt,
    getOppositeDirectionCode(direction),
    amount,
    markedPrice
  ].join('\t')

export const hasBlastSpread = (spread: string | null) =>
  !!spread?.trim() && !isNaN(Number(spread))

export const getTradeBlastItems = ({
  spread,
  isTreasury,
  copy
}: {
  spread: string | null
  isTreasury: boolean
  copy: (isSpread: boolean) => void
}) =>
  [false, true].map((isSpread) => {
    const disabled = isSpread && (isTreasury || !hasBlastSpread(spread))
    return {
      name: `Copy Trade Blast (${isSpreadToLabel(isSpread)})`,
      disabled,
      tooltip: disabled ? 'No spread calc available' : undefined,
      action: () => copy(isSpread)
    }
  })

export const formatTradeBlast = ({
  boardLabel,
  isin,
  isTreasury,
  currency,
  spread,
  price,
  isSpread
}: {
  boardLabel: string | null
  isin: string | null
  isTreasury: boolean
  currency: string
  spread: string | null
  price: string | null
  isSpread: boolean
}) => {
  const label = boardLabel?.replace(/ \d{1,2}\/(\d{2})$/, ' $1') ?? ''
  const signedSpread = spread?.startsWith('-') ? spread : `+${spread}`
  const numericPrice = Number(price)
  const blastPrice =
    isTreasury && numericPrice > 0
      ? formatTreasuryPrice(numericPrice)
      : price ?? ''
  const value = isSpread ? signedSpread : formatCurrency(blastPrice, currency)
  return `${label} ${isin ?? ''} TRADES ${value}`
}

export const trimUppercase = (value?: string) => {
  return value?.trim().toUpperCase() ?? ''
}
